import React, { useMemo, useState } from "react";
import { MdOutlineCancel } from "react-icons/md";
import { useLocation } from "react-router-dom";
import useTagList from "../../hooks/useTagList";
import { useUIContext } from "../../context/UIContext";
import HelpHyperLink from "../../unicomponents/HelpHyperLink";
import { useTranslation } from "react-i18next";

export default function TagSetting() {
  const location = useLocation();
  const paths = location.pathname.split("/");
  // 배열의 두 번째 요소를 schoolId로 사용합니다.
  const schoolId = paths[1];
  //아래 조건으로 데이터를 불러오도록 합니다.
  const conditions = {
    schoolid: schoolId,
  };
  const { t } = useTranslation();
  const { setIsLoadingModal } = useUIContext();

  const {
    tagListQuery: { data: tagList },
    createOrUpdateTagList,
  } = useTagList(conditions);

  const [inputTag, setInputTag] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputTag((inputTag) => ({ ...inputTag, [name]: value }));
    // console.log("inputTag", inputTag);
  };

  const addTag = (entity) => {
    if (entity && inputTag[entity] && inputTag[entity].trim().length > 0) {
      // 현재 태그 리스트에서 entity_type과 schoolId가 일치하는 항목 찾기
      const existingTagListIndex = tagList.findIndex(
        (tagItem) =>
          tagItem.entity_type === entity && tagItem.schoolid === schoolId
      );

      let updatedTags = [];
      if (existingTagListIndex !== -1) {
        // 태그 리스트에 이미 존재하는 태그인지 확인
        const existingTags = tagList[existingTagListIndex].tags;
        if (existingTags.includes(inputTag[entity])) {
          console.error("Tag already exists. Skipping addition.");
          return; // 중복되는 태그가 있으면 여기서 함수 종료
        }
        // 새 태그를 기존 태그 리스트에 추가
        updatedTags = [...existingTags, inputTag[entity]];
      } else {
        // 일치하는 entity_type이 없는 경우 새로운 태그로 시작
        updatedTags = [inputTag[entity]];
      }

      // 태그 리스트 업데이트 준비
      const tagToUpdate = {
        schoolid: schoolId,
        entity_type: entity,
        tags: updatedTags,
      };
      setIsLoadingModal(true);
      // 백엔드로 업데이트 요청
      createOrUpdateTagList.mutate(tagToUpdate, {
        onSuccess: (successMessage) => {
          console.log(successMessage);
          setIsLoadingModal(false);
          // 성공적으로 업데이트되면 input 필드 초기화
          setInputTag({ ...inputTag, [entity]: "" });
          // 태그 리스트 상태 업데이트 (옵션)
        },
        onError: (error) => {
          setIsLoadingModal(false);
          console.error(error);
          alert(t("common.messages.errorAlert") + error.message);
        },
      });
    }
  };

  const removeTag = (entityType, tagToRemove) => {
    // 현재 엔티티 타입과 일치하며 schoolId가 같은 태그 목록을 찾습니다.
    const existingTagList = tagList.find(
      (tagItem) =>
        tagItem.entity_type === entityType && tagItem.schoolid === schoolId
    );

    if (existingTagList) {
      // 클릭한 태그를 제외한 새로운 태그 목록을 생성합니다.
      const updatedTags = existingTagList.tags.filter(
        (tag) => tag !== tagToRemove
      );

      // 업데이트할 태그 데이터 객체를 준비합니다.
      const tagToUpdate = {
        schoolid: schoolId,
        entity_type: entityType,
        tags: updatedTags,
      };

      setIsLoadingModal(true);
      // 서버에 업데이트 요청을 보냅니다.
      createOrUpdateTagList.mutate(tagToUpdate, {
        onSuccess: () => {
          console.log("Tag successfully updated.");
          setIsLoadingModal(false);
        },
        onError: (error) => {
          console.error("Error updating tag:", error);
          setIsLoadingModal(false);
          alert("Error occurred. Please try again.");
        },
      });
    }
  };

  const renderTags = useMemo(() => {
    return (entityType) => {
      const tagItems = tagList?.filter(
        (tagItem) =>
          tagItem.entity_type === entityType && tagItem.schoolid === schoolId
      )[0]?.tags;

      return tagItems?.map((tag, idx) => (
        <span key={idx} style={{ marginRight: "8px" }}>
          {tag}
          <MdOutlineCancel
            role="button"
            onClick={() => removeTag(entityType, tag)}
          />
        </span>
      ));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagList, schoolId]); // 의존성 배열에 tagList와 schoolId를 포함

  return (
    <div className="basicWidth">
      <div className="container p-4">
        <h3 className="fw-bold">
          {t("admin.tagSetting.tagSetting")}{" "}
          <HelpHyperLink link={"https://youtu.be/ZgKP-UOGDIo"} />
        </h3>
        <p>
          {t("admin.tagSetting.tagSettingDescription")}
          <br />
          {t("admin.tagSetting.tagSettingDescription2")}
        </p>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: "20%" }}>{t("admin.tagSetting.page")}</th>
              <th style={{ width: "20%" }}>{t("admin.tagSetting.tagInput")}</th>
              <th style={{ width: "10%" }}></th>
              <th style={{ width: "50%" }}>
                {t("admin.tagSetting.currentTag")}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td valign="middle">{t("admin.tagSetting.lesson")}</td>
              <td>
                <input
                  className="form-control"
                  name="lessons"
                  value={inputTag?.lessons || ""}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addTag("lessons"); // Enter 키를 눌렀을 때 handleSubmit 호출
                    }
                  }}
                />
              </td>
              <td>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => addTag("lessons")}
                >
                  {t("common.labels.add")}
                </button>
              </td>
              <td valign="middle">{renderTags("lessons")} </td>
            </tr>
            <tr>
              <td valign="middle">
                {t("admin.tagSetting.educationalActivities")}
              </td>
              <td>
                <input
                  className="form-control"
                  name="educational_activities"
                  value={inputTag?.educational_activities || ""}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      addTag("educational_activities"); // Enter 키를 눌렀을 때 handleSubmit 호출
                    }
                  }}
                />
              </td>
              <td>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => addTag("educational_activities")}
                >
                  {t("common.labels.add")}
                </button>
              </td>
              <td valign="middle">{renderTags("educational_activities")} </td>
            </tr>
          </tbody>
        </table>
        <p>
          {t("admin.tagSetting.tagSettingDescription3")}
          <br />
          {t("admin.tagSetting.tagSettingDescription4")}
        </p>
      </div>
    </div>
  );
}
